// Bladindeling van de boorprofielen-export: staand A4 met dezelfde kop (titel, project, logo) en
// voet als het projectrapport. Elke boring staat er compleet: kop, profiel, laagbeschrijvingen en
// de eigen legenda rechts ernaast, over de volle inhoudsbreedte. Zo blijft de tekst even groot
// als in de Boorprofiel-tab (een halve breedte maakte alles te klein). De profielen staan onder
// elkaar, zoveel als er op de pagina passen.
//
// Per boring de schaal: we streven naar drie boringen per pagina, maar nooit grover dan 1:100
// om dat te halen; alleen een boring die op 1:100 niet op een hele pagina past, gaat grover.
// Ruimte die de laagbeschrijvingen of de legenda toch al innemen, mag het profiel gebruiken.
//
// Puur (geen canvas of jsPDF), zodat het te testen is; het tekenen zit in boorprofiel-pdf.js.
import { renderAtScale, shrinkToFit, VERTICAL_SCALES, svgSize } from './boorprofiel-scale.js';
import { buildGedeeldeLegendaSvg } from './boorprofiel.js';
import { PAGE } from './pdf-page.js';

export const ROW_GAP = 6;
export const LABEL_H = 4;      // strook boven elk profiel voor "Schaal verticaal 1:N"
export const CONTENT_TOP = PAGE.MT + 2, CONTENT_BOTTOM = PAGE.PH - PAGE.MB - 1;
export const CONTENT_W = PAGE.PW - PAGE.ML - PAGE.MR;
export const CONTENT_H = CONTENT_BOTTOM - CONTENT_TOP;

/** Zoveel boringen per pagina streven we na (als ze niet grover dan 1:100 hoeven). */
export const TARGET_PER_PAGE = 3;
/** Grover dan dit alleen als de boring anders niet op een pagina past. */
export const COARSEST_READABLE_SCALE = 100;

/**
 * Schaal voor één boring (met eigen legenda) over de breedte widthMm. Hoogtes zijn inclusief de
 * strook voor het schaallabel.
 * @param {object} profile  `d`-object voor buildBoorprofielSvg
 * @param {{widthMm?:number, targetHeightMm?:number, maxHeightMm?:number}} [box]
 * @returns {{d:object, scaled:object, height:number}}
 */
export function scaleForBlock(profile, {
  widthMm = CONTENT_W,
  targetHeightMm = (CONTENT_H - (TARGET_PER_PAGE - 1) * ROW_GAP) / TARGET_PER_PAGE,
  maxHeightMm = CONTENT_H,
  hideLegend = false,
} = {}) {
  const d = { ...profile, hideLegend };
  const at = new Map(VERTICAL_SCALES.map(s => [s, null]));
  const render = (s) => at.get(s) || at.set(s, renderAtScale(d, widthMm, s)).get(s);
  const blockH = (s) => LABEL_H + render(s).drawH;
  // Wat de tekst en legenda op 1:100 al vragen, mag het profiel gebruiken.
  const budget = Math.min(maxHeightMm, Math.max(targetHeightMm, blockH(COARSEST_READABLE_SCALE)));
  const scale = VERTICAL_SCALES.find(s => blockH(s) <= budget);
  const scaled = scale ? render(scale) : shrinkToFit(render(VERTICAL_SCALES.at(-1)), maxHeightMm - LABEL_H);
  return { d, scaled, height: LABEL_H + scaled.drawH };
}

/**
 * Verdeelt de profielen over pagina's: onder elkaar, zoveel als er passen, in de gegeven volgorde.
 * @param {object[]} profiles  `d`-objecten
 * @returns {{pages:Array<Array<{d:object, scaled:object, y:number, height:number}>>}}
 */
export function layoutBoorprofielPages(profiles) {
  const pages = [];
  let page = null, y = 0;
  for (const p of profiles) {
    const { d, scaled, height } = scaleForBlock(p);
    if (!page || y + height > CONTENT_BOTTOM + 1e-6) { page = []; pages.push(page); y = CONTENT_TOP; }
    page.push({ d, scaled, y, height });
    y += height + ROW_GAP;
  }
  return { pages };
}

// ── Twee naast elkaar (de boorprofielen-export en de bijlage van het rapport) ─────────────
//
// Sinds 30-10-2026: twee kolommen, hooguit twee rijen -- vier boringen per pagina. Een diepe
// boring (die op 1:100 niet in een halve pagina past) krijgt de hele hoogte; dan staan er twee
// naast elkaar. Twee profielen staan altijd naast elkaar: een rij is een paar, en wat niet meer
// op de pagina past gaat als paar naar de volgende.
// Zonder legenda per profiel (dat maakte de tekst op halve breedte te klein) en zonder
// schaalregel per profiel: de schaal staat in de voet van de pagina (schaalTekst), de legenda één
// keer, aan het eind (onder de laatste profielen als dat past, anders op een eigen pagina).

export const COLS = 2;
export const COL_GAP = 8;
export const COL_W = (CONTENT_W - (COLS - 1) * COL_GAP) / COLS;
/** px per mm van de legenda: dezelfde tekstgrootte als in een profiel op halve breedte. */
const LEGENDA_PX_PER_MM = 5.2;
const LEGENDA_GAP = 6;
/** Hooguit zoveel rijen per pagina: twee rijen van twee = vier boringen. */
export const MAX_RIJEN = 2;

/** De legenda voor een set profielen, op paginabreedte; hoogte in mm. */
export function legendaVoor(profiles) {
  const leg = buildGedeeldeLegendaSvg(profiles, { width: Math.round(CONTENT_W * LEGENDA_PX_PER_MM) });
  return { ...leg, drawW: CONTENT_W, drawH: leg.h / LEGENDA_PX_PER_MM };
}

/**
 * Verdeelt de profielen in paren over pagina's, met de legenda aan het eind.
 * @param {object[]} profiles  `d`-objecten
 * @returns {{pages:Array<{cells:Array<{d:object, scaled:object, x:number, y:number, height:number}>, legendaY?:number}>, legenda:object|null}}
 *   Een pagina met `legendaY` krijgt de legenda (de laatste, of een eigen pagina zonder cellen).
 */
export function layoutBoorprofielRaster(profiles) {
  if (!profiles?.length) return { pages: [], legenda: null };
  const rijHoogte = (CONTENT_H - ROW_GAP) / 2;
  const cellen = profiles.map((p) => scaleForBlock(p, { widthMm: COL_W, targetHeightMm: rijHoogte, maxHeightMm: CONTENT_H, hideLegend: true }));
  const pages = [];
  let page = null, y = 0, rijen = 0;
  for (let i = 0; i < cellen.length; i += COLS) {
    const rij = cellen.slice(i, i + COLS);
    // Geen schaalregel boven het profiel meer: die strook valt weg.
    const hoogte = Math.max(...rij.map((c) => c.height - LABEL_H));
    if (!page || rijen >= MAX_RIJEN || y + hoogte > CONTENT_BOTTOM + 1e-6) {
      page = { cells: [] };
      pages.push(page);
      y = CONTENT_TOP;
      rijen = 0;
    }
    rijen += 1;
    rij.forEach((c, k) => page.cells.push({ ...c, x: PAGE.ML + k * (COL_W + COL_GAP), y, height: c.height - LABEL_H }));
    y += hoogte + ROW_GAP;
  }
  // De legenda één keer: onder de laatste profielen als hij past, anders op een eigen pagina.
  const legenda = legendaVoor(profiles);
  const laatste = pages.at(-1);
  const onder = Math.max(...laatste.cells.map((c) => c.y + c.height));
  if (onder + LEGENDA_GAP + legenda.drawH <= CONTENT_BOTTOM + 1e-6) laatste.legendaY = CONTENT_BOTTOM - legenda.drawH;
  else pages.push({ cells: [], legendaY: CONTENT_TOP });
  return { pages, legenda };
}

/**
 * De verticale schaal van een pagina, voor in de voet: 'Schaal verticaal 1:20', of per boring als
 * er verschillende op staan ('Schaal verticaal 01, 02 1:20 · 03 1:100').
 */
export function schaalTekst(page) {
  if (!page.cells.length) return '';
  const label = (s) => `${s.approximate ? 'ca. ' : ''}1:${s.scale}`;
  const groepen = new Map();
  for (const c of page.cells) {
    const k = label(c.scaled);
    if (!groepen.has(k)) groepen.set(k, []);
    groepen.get(k).push(c.d.header?.name || '?');
  }
  if (groepen.size === 1) return `Schaal verticaal ${[...groepen.keys()][0]}`;
  return `Schaal verticaal ${[...groepen].map(([k, namen]) => `${namen.join(', ')} ${k}`).join(' · ')}`;
}

/**
 * Eén pagina als één SVG in millimeters (de inhoud tussen kop en voet): de profielen, en op de
 * pagina met `legendaY` de legenda. Voor de PDF en voor de Word-bijlage, zodat die er hetzelfde
 * uitzien.
 */
export function paginaSvg(page, legenda = null) {
  const nest = (svg, x, y, w, h) => {
    const { w: vw, h: vh } = svgSize(svg);
    // Het lettertype staat in de style van de buitenste svg; die moet mee, anders valt de tekst
    // terug op een schreefletter.
    const style = (/^<svg[^>]*style="([^"]*)"/.exec(svg) || [])[1] || '';
    const font = (/font-family:[^;"]+/.exec(style) || [''])[0];
    return svg.replace(/^<svg[^>]*>/, `<svg x="${x}" y="${y}" width="${w}" height="${h}" viewBox="0 0 ${vw} ${vh}" style="${font}">`);
  };
  const delen = page.cells.map((c) => nest(c.scaled.svg, c.x - PAGE.ML, c.y - CONTENT_TOP, c.scaled.drawW, c.scaled.drawH));
  if (legenda && page.legendaY != null) delen.push(nest(legenda.svg, 0, page.legendaY - CONTENT_TOP, legenda.drawW, legenda.drawH));
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CONTENT_W} ${CONTENT_H}" width="100%">${delen.join('')}</svg>`;
}
